import { confirmSignUp, signIn, signUp } from "aws-amplify/auth";
import { Leaf, Mail, UserPlus } from "lucide-react";
import React, { useState } from "react";

interface SignupFormProps {
  onSuccess: () => void;
  onSwitchToLogin: () => void;
}

const SignupForm: React.FC<SignupFormProps> = ({
  onSuccess,
  onSwitchToLogin,
}) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState<"signup" | "verify">("signup");

  const tryAutoSignIn = async () => {
    try {
      await signIn({
        username: email,
        password: password,
      });
      onSuccess();
      return true;
    } catch (err) {
      console.log("Auto sign-in failed:", err);
      return false;
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      setLoading(false);
      return;
    }

    try {
      await signUp({
        username: email,
        password: password,
        options: {
          userAttributes: {
            email: email,
          },
        },
      });

      setStep("verify");
    } catch (err: any) {
      console.error("Signup error:", err);
      if (err.message.includes("Access denied")) {
        setError(
          "Access denied. This application is restricted to authorized users only. Please contact an administrator if you believe you should have access."
        );
      } else if (err.message.includes("already exists")) {
        setError(
          "An account with this email already exists. Please try signing in instead."
        );
      } else {
        setError(err.message || "Signup failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await confirmSignUp({
        username: email,
        confirmationCode: verificationCode,
      });

      onSuccess();
    } catch (err: any) {
      console.error("Verification error:", err);
      if (err.message.includes("Current status is CONFIRMED")) {
        setError("Account already verified. Attempting to sign you in...");
        const signInSuccess = await tryAutoSignIn();
        if (!signInSuccess) {
          setTimeout(() => {
            setError("Account verified. Please use the sign in form.");
            onSwitchToLogin();
          }, 2000);
        }
      } else if (err.message.includes("Invalid verification code")) {
        setError(
          "Invalid verification code. Please check your email and try again."
        );
      } else if (err.message.includes("Attempt limit exceeded")) {
        setError("Too many attempts. Please request a new verification code.");
      } else {
        setError(err.message || "Verification failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (step === "verify") {
    return (
      <div className="min-h-screen bg-earth-50 flex flex-col justify-center items-center py-12 px-4">
        {/* Header */}
        <div className="text-center mb-8 animate-fade-in">
          <div className="mx-auto w-14 h-14 bg-harvest rounded-full flex items-center justify-center shadow-card mb-5">
            <Mail className="w-7 h-7 text-white" strokeWidth={2.5} />
          </div>
          <h1 className="font-display text-3xl text-earth-900">
            Verify your email
          </h1>
          <p className="mt-2 text-earth-600">
            A code has been sent to{" "}
            <span className="font-semibold text-earth-900">{email}</span>
          </p>
        </div>

        {/* Form Card */}
        <div className="w-full max-w-sm card p-8 animate-slide-up">
          {error && (
            <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-alert-urgent text-sm text-center">{error}</p>
            </div>
          )}

          <form onSubmit={handleVerification} className="space-y-5">
            <div>
              <label htmlFor="verificationCode" className="label">
                Verification Code
              </label>
              <input
                id="verificationCode"
                name="verificationCode"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                required
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                className="input text-center tracking-widest font-mono text-lg"
                placeholder="000000"
                maxLength={6}
              />
              <p className="mt-2 text-xs text-earth-500 text-center">
                Enter the 6-digit code from your email
              </p>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={loading || verificationCode.length < 6}
                className="w-full btn-primary py-3 text-base"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Verifying...
                  </div>
                ) : (
                  "Verify Email"
                )}
              </button>
            </div>

            <div className="text-center pt-2">
              <button
                type="button"
                onClick={() => setStep("signup")}
                className="text-sm font-medium text-field hover:text-field-600 hover:underline"
              >
                Resend code or change email
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Signup Step
  return (
    <div className="min-h-screen bg-earth-50 flex flex-col justify-center items-center py-12 px-4">
      {/* Header */}
      <div className="text-center mb-8 animate-fade-in">
        <div className="mx-auto w-14 h-14 bg-field rounded-full flex items-center justify-center shadow-card mb-5">
          <UserPlus className="w-7 h-7 text-white" strokeWidth={2.5} />
        </div>
        <h1 className="font-display text-3xl text-earth-900">
          Create your account
        </h1>
        <p className="mt-2 text-earth-600">Join Waddington Park Farm Tasks</p>
      </div>

      {/* Form Card */}
      <div className="w-full max-w-sm card p-8 animate-slide-up">
        {error && (
          <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-alert-urgent text-sm text-center">{error}</p>
          </div>
        )}

        <form onSubmit={handleSignup} className="space-y-5">
          <div>
            <label htmlFor="email" className="label">
              Email address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              placeholder="you@example.com"
            />
          </div>

          <div>
            <label htmlFor="password" className="label">
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="new-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
              placeholder="Create a password"
            />
            <p className="mt-1.5 text-xs text-earth-500">
              8+ characters with uppercase, lowercase, and a number
            </p>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="label">
              Confirm Password
            </label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="input"
              placeholder="Confirm your password"
            />
          </div>

          <div className="pt-2">
            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-3 text-base"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Creating account...
                </div>
              ) : (
                "Create account"
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Login link */}
      <div className="mt-6 w-full max-w-sm animate-fade-in" style={{ animationDelay: "100ms" }}>
        <div className="py-4 px-4 card-warm text-center">
          <p className="text-earth-600">
            Already have an account?{" "}
            <button
              type="button"
              onClick={onSwitchToLogin}
              className="font-semibold text-field hover:text-field-600 hover:underline"
            >
              Sign in
            </button>
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-12 text-center">
        <p className="text-sm text-earth-500">
          &copy; {new Date().getFullYear()} Waddington Park. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default SignupForm;
